import { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, Button, TextField, MenuItem,
  Select, FormControl, InputLabel, Chip, IconButton, InputAdornment,
  useTheme, useMediaQuery, Card, CardContent, Tooltip, Tabs, Tab,
  Dialog, DialogTitle, DialogContent, DialogActions,
  FormControlLabel, Checkbox, CircularProgress
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import PostAddIcon from '@mui/icons-material/PostAdd';
import PrintIcon from '@mui/icons-material/Print';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { toast } from 'react-toastify';
import BrowserTitle from '../components/Helmet';
import WarrantyTicketDialog from '../components/WarrantyTicketDialog';
import { AuthContext } from '../context/AuthProvider';
import warrantyTicketServices from '../services/warrantyTicketServices';
import userServices from '../services/userServices';
import { stringifyAddress } from '../utils/formatter';
import { table_container_base, table_header_with_search } from '../static/styles';
import { usePagination } from '../hooks/usePagination';

const ACTIVE_STATUSES = ['', 'Active', 'In Progress'];
const PRIORITIES = ['', 'High', 'Medium', 'Low'];

const priorityColor = {
  High: '#d32f2f',
  Medium: '#ed6c02',
  Low: '#2e7d32',
};

const statusColor = {
  Active: 'primary',
  'In Progress': 'warning',
  Resolved: 'success',
};

const claimStatusChip = (status) =>
  status === 'CLOSED'
    ? { label: 'CLAIM CLOSED', sx: { bgcolor: '#2e7d32', color: '#fff', fontWeight: 700 } }
    : { label: 'SENT', sx: { bgcolor: '#d32f2f', color: '#fff', fontWeight: 700 } };

const workStatusChip = {
  'Completed on-site': { color: 'success', variant: 'outlined' },
  'Parts needed': { color: 'warning', variant: 'filled' },
  'Parts ordered': { color: 'warning', variant: 'outlined' },
  'Parts received': { color: 'info', variant: 'filled' },
  'Follow-up scheduled': { color: 'secondary', variant: 'outlined' },
  'Follow-up needed': { color: 'secondary', variant: 'filled' },
};

export default function WarrantyTickets() {
  const { userData } = useContext(AuthContext);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { rowsPerPage, page, setPage, handleChangePage, handleChangeRowsPerPage } = usePagination();

  const [activeTab, setActiveTab] = useState(0);
  const [tickets, setTickets] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [resolvedCount, setResolvedCount] = useState(0);
  const [activeCount, setActiveCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [filterTechId, setFilterTechId] = useState('');
  const [technicians, setTechnicians] = useState([]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedTicketId, setSelectedTicketId] = useState(null);

  const [labelDialogOpen, setLabelDialogOpen] = useState(false);
  const [labelTicket, setLabelTicket] = useState(null);
  const [labelDate, setLabelDate] = useState('');

  const [claims, setClaims] = useState([]);
  const [claimsLoading, setClaimsLoading] = useState(false);
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [claimMoneyReceived, setClaimMoneyReceived] = useState(false);
  const [claimSaving, setClaimSaving] = useState(false);

  const isAdmin = userData?.role === 'superadmin' || userData?.role === 'admin';
  const isDispatcher = userData?.role === 'dispatcher';
  const isTechnician = userData?.role === 'technician';
  const canCreate = isAdmin || isTechnician;
  const showTechFilter = isAdmin || isDispatcher;
  const showClaimsTab = isAdmin;
  const claimsTabActive = showClaimsTab && activeTab === 2;

  const effectiveStatus = activeTab === 1
    ? 'Resolved'
    : filterStatus || 'Active,In Progress';

  const fetchClaims = useCallback(async () => {
    if (!showClaimsTab) return;
    setClaimsLoading(true);
    try {
      const res = await warrantyTicketServices.scaClaimsList();
      if (res.status === 200) setClaims(res.data || []);
    } catch (e) {
      console.error('Error fetching SCA claims:', e);
    }
    setClaimsLoading(false);
  }, [showClaimsTab]);

  useEffect(() => {
    fetchClaims();
  }, [fetchClaims]);

  const fetchTickets = useCallback(async () => {
    if (claimsTabActive) return;
    setLoading(true);
    try {
      const response = await warrantyTicketServices.getList(
        page * rowsPerPage,
        rowsPerPage,
        searchQuery,
        effectiveStatus,
        filterPriority,
        filterTechId
      );
      if (response.status === 200 || response.status === 206) {
        setTickets(response.data?.data || []);
        setTotalCount(response.data?.total || 0);
        if (activeTab === 0) setActiveCount(response.data?.total || 0);
        else setResolvedCount(response.data?.total || 0);
      }
    } catch (e) {
      console.error('Error fetching warranty tickets:', e);
    }
    setLoading(false);
  }, [page, rowsPerPage, searchQuery, effectiveStatus, filterPriority, filterTechId, activeTab, claimsTabActive]);

  useEffect(() => {
    const fetchOtherTabCount = async () => {
      if (claimsTabActive) {
        try {
          const [activeRes, resolvedRes] = await Promise.all([
            warrantyTicketServices.getList(0, 1, '', 'Active,In Progress', '', filterTechId),
            warrantyTicketServices.getList(0, 1, '', 'Resolved', '', filterTechId),
          ]);
          if (activeRes.status === 200 || activeRes.status === 206) setActiveCount(activeRes.data?.total || 0);
          if (resolvedRes.status === 200 || resolvedRes.status === 206) setResolvedCount(resolvedRes.data?.total || 0);
        } catch (_) {}
        return;
      }
      try {
        const otherStatus = activeTab === 0 ? 'Resolved' : 'Active,In Progress';
        const res = await warrantyTicketServices.getList(0, 1, '', otherStatus, '', filterTechId);
        if (res.status === 200 || res.status === 206) {
          if (activeTab === 0) setResolvedCount(res.data?.total || 0);
          else setActiveCount(res.data?.total || 0);
        }
      } catch (_) {}
    };
    fetchOtherTabCount();
  }, [activeTab, filterTechId, claimsTabActive]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  useEffect(() => {
    if (showTechFilter) {
      userServices.getAllUsers(0, 100, false).then((res) => {
        if (res.status === 200 || res.status === 206) {
          const techs = (res.data?.data || []).filter(u => u.role === 'technician');
          setTechnicians(techs);
        }
      }).catch(() => {});
    }
  }, [showTechFilter]);

  useEffect(() => {
    setPage(0);
  }, [searchQuery, filterStatus, filterPriority, filterTechId, activeTab, setPage]);

  const handleTabChange = (_, newValue) => {
    setActiveTab(newValue);
    setFilterStatus('');
    setSearchQuery('');
    setFilterPriority('');
  };

  const handleClearSearch = () => {
    setSearchQuery('');
  };

  const handleCreatePO = async (e, ticketId) => {
    e.stopPropagation();
    try {
      const res = await warrantyTicketServices.createProposal(ticketId);
      if (res.status === 200 || res.status === 201) {
        toast.success(`Proposal ${res.data.publicId} created`);
        navigate(`/proposals/${res.data.proposalId}`);
      }
    } catch (err) {
      const msg = err?.response?.data?.message || 'Error creating proposal';
      toast.error(msg);
      console.error(err);
    }
  };

  const openTicketDialog = (ticketId) => {
    setSelectedTicketId(ticketId);
    setDialogOpen(true);
  };

  const openClaimDialog = (claim) => {
    setSelectedClaim(claim);
    setClaimMoneyReceived(Boolean(claim.moneyReceived));
  };

  const downloadClaimPdf = async (claim) => {
    try {
      const res = await warrantyTicketServices.scaClaimPdf(claim.id);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = claim.pdfFilename || `SCA-claim-${claim.claimNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.status === 404 ? 'PDF not stored for this claim' : 'Error downloading PDF');
    }
  };

  const saveClaim = async () => {
    if (!selectedClaim) return;
    setClaimSaving(true);
    try {
      const res = await warrantyTicketServices.scaClaimUpdate(selectedClaim.id, claimMoneyReceived);
      if (res.status === 200) {
        toast.success(claimMoneyReceived ? `Claim #${selectedClaim.claimNumber} closed` : `Claim #${selectedClaim.claimNumber} reopened`);
        setSelectedClaim(null);
        fetchClaims();
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Error updating claim');
    }
    setClaimSaving(false);
  };

  const openClaimsCount = claims.filter((c) => c.status !== 'CLOSED').length;

  const filteredClaims = claims.filter((c) => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return true;
    return [c.claimNumber, c.ticketNumber, c.po, c.address, c.customer]
      .some((v) => String(v || '').toLowerCase().includes(q));
  });
  const pagedClaims = filteredClaims.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric'
    });
  };

  const getAddress = (ticket) => {
    if (ticket.property) {
      return stringifyAddress(ticket.property);
    }
    return '—';
  };

  const getCustomerName = (ticket) => {
    if (ticket.client) {
      return `${ticket.client.firstName || ''} ${ticket.client.lastName || ''}`.trim() || '—';
    }
    return '—';
  };

  const getCreatedByName = (ticket) => {
    if (ticket.createdBy) {
      return `${ticket.createdBy.firstName || ''} ${ticket.createdBy.lastName || ''}`.trim();
    }
    if (ticket.assignedTech) {
      return `${ticket.assignedTech.firstName || ''} ${ticket.assignedTech.lastName || ''}`.trim();
    }
    return '—';
  };

  const openLabelDialog = (e, ticket) => {
    e.stopPropagation();
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const yyyy = tomorrow.getFullYear();
    const mm = String(tomorrow.getMonth() + 1).padStart(2, '0');
    const dd = String(tomorrow.getDate()).padStart(2, '0');
    setLabelDate(`${yyyy}-${mm}-${dd}`);
    setLabelTicket(ticket);
    setLabelDialogOpen(true);
  };

  const escHtml = (str) => String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

  const printLabel = () => {
    if (!labelTicket) return;
    const ticket = labelTicket;
    const address = escHtml(getAddress(ticket));
    const techName = escHtml(getCreatedByName(ticket));
    const ticketNum = escHtml(ticket.ticketNumber || '');
    const originalPO = escHtml(
      ticket.notAirmaxxInstallation
        ? 'Not Airmaxx Installation'
        : (ticket.proposal?.publicId || ticket.legacyProposalNumber || '—')
    );
    const defModel = ticket.defectiveModel || '';
    const defSerial = ticket.defectiveSerial || '';
    const defLine = escHtml([defModel, defSerial ? `S/N: ${defSerial}` : ''].filter(Boolean).join(' • '));
    const chosenDate = labelDate
      ? new Date(labelDate + 'T00:00:00').toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
      : new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });

    const html = `<!DOCTYPE html>
<html><head><title>Label ${ticketNum}</title>
<style>
  @page { size: 4in 6in; margin: 0; }
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { width: 4in; height: 6in; font-family: Arial, Helvetica, sans-serif; padding: 0.25in; display: flex; flex-direction: column; }
  .header { text-align: center; border-bottom: 3px solid #000; padding-bottom: 8px; margin-bottom: 10px; }
  .header h1 { font-size: 28px; letter-spacing: 1px; }
  .header .subtitle { font-size: 11px; color: #555; margin-top: 2px; }
  .row { display: flex; margin-bottom: 8px; }
  .label { font-size: 11px; font-weight: bold; color: #333; min-width: 105px; text-transform: uppercase; }
  .value { font-size: 14px; font-weight: 600; flex: 1; }
  .value.large { font-size: 18px; }
  .divider { border-top: 1px dashed #999; margin: 8px 0; }
  .defective { background: #f5f5f5; border: 1px solid #ddd; border-radius: 4px; padding: 8px 10px; margin: 6px 0; }
  .defective .title { font-size: 11px; font-weight: bold; text-transform: uppercase; color: #c62828; margin-bottom: 4px; }
  .defective .info { font-size: 14px; font-weight: 600; }
  .footer { margin-top: auto; border-top: 2px solid #000; padding-top: 8px; text-align: center; }
  .footer .company { font-size: 12px; font-weight: bold; color: #555; }
  @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
</style></head><body>
  <div class="header">
    <h1>${ticketNum}</h1>
    <div class="subtitle">WARRANTY REPLACEMENT LABEL</div>
  </div>
  <div class="row"><span class="label">Original PO:</span><span class="value large">${originalPO}</span></div>
  <div class="divider"></div>
  <div class="row"><span class="label">Address:</span><span class="value">${address}</span></div>
  <div class="row"><span class="label">Technician:</span><span class="value">${techName}</span></div>
  <div class="divider"></div>
  ${defLine ? `<div class="defective"><div class="title">Defective Equipment</div><div class="info">${defLine}</div></div>` : ''}
  <div class="row"><span class="label">Replaced:</span><span class="value large">${chosenDate}</span></div>
  <div class="footer"><div class="company">Airmaxx Heating &amp; Air Conditioning</div></div>
</body></html>`;

    const win = window.open('', '_blank', 'width=450,height=650');
    if (!win) {
      toast.error('Pop-up blocked — please allow pop-ups for this site and try again');
      return;
    }
    win.document.write(html);
    win.document.close();
    win.onload = () => { win.print(); };
    setLabelDialogOpen(false);
    setLabelTicket(null);
  };

  const renderMobileCard = (ticket) => (
    <Card
      key={ticket.id}
      sx={{ mb: 2, cursor: 'pointer', borderLeft: `4px solid ${priorityColor[ticket.priority] || '#999'}` }}
      onClick={() => openTicketDialog(ticket.id)}
    >
      <CardContent sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            {ticket.ticketNumber}
          </Typography>
          <Chip
            label={ticket.status}
            color={statusColor[ticket.status] || 'default'}
            size="small"
          />
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
          {getAddress(ticket)}
        </Typography>
        <Typography variant="body2" sx={{ mb: 0.5 }}>
          {getCustomerName(ticket)} {ticket.customerPhone ? `• ${ticket.customerPhone}` : ''}
        </Typography>
        <Typography variant="body2" color="text.secondary" noWrap sx={{ mb: 0.5 }}>
          {ticket.issueDescription}
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
            <Chip
              label={ticket.priority}
              size="small"
              sx={{ backgroundColor: priorityColor[ticket.priority], color: '#fff', fontWeight: 600 }}
            />
            {ticket.workStatus && (
              <Chip
                label={ticket.workStatus}
                size="small"
                color={workStatusChip[ticket.workStatus]?.color || 'default'}
                variant={workStatusChip[ticket.workStatus]?.variant || 'outlined'}
              />
            )}
          </Box>
          <Typography variant="caption" color="text.secondary">
            {getCreatedByName(ticket)} • {activeTab === 1 ? formatDate(ticket.resolvedAt) : formatDate(ticket.createdAt)}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
          {ticket.parts && ticket.parts.length > 0 ? (
            <Typography variant="caption" color="warning.main">
              {ticket.parts.length} part(s) ordered
              {ticket.parts[0]?.expectedArrival && ` • ETA: ${formatDate(ticket.parts[0].expectedArrival)}`}
            </Typography>
          ) : <span />}
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip title="Create Service Proposal" arrow>
              <IconButton size="small" color="primary" onClick={(e) => handleCreatePO(e, ticket.id)}>
                <PostAddIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {activeTab === 0 && (
              <Tooltip title="Print Label" arrow>
                <IconButton size="small" onClick={(e) => openLabelDialog(e, ticket)}>
                  <PrintIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ p: { xs: 1, md: 3 } }}>
      <BrowserTitle title="Warranty Tickets" />

      <Box sx={{ ...table_header_with_search, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          {isTechnician ? 'My Warranty Tickets' : 'Warranty Tickets'}
        </Typography>
        {canCreate && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/warranty-tickets/create')}
            size={isMobile ? 'large' : 'medium'}
            sx={isMobile ? { width: '100%', py: 1.5 } : {}}
          >
            New Ticket
          </Button>
        )}
      </Box>

      <Paper sx={{ mb: 2 }}>
        <Tabs value={activeTab} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider', px: 1 }}>
          <Tab label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              Active
              {activeCount > 0 && (
                <Chip label={activeCount} size="small" color="primary" sx={{ height: 20, fontSize: '0.75rem', fontWeight: 700, minWidth: 28 }} />
              )}
            </Box>
          } />
          <Tab label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              Resolved
              {resolvedCount > 0 && (
                <Chip label={resolvedCount} size="small" color="success" sx={{ height: 20, fontSize: '0.75rem', fontWeight: 700, minWidth: 28 }} />
              )}
            </Box>
          } />
          {showClaimsTab && (
            <Tab label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                Claims
                {openClaimsCount > 0 && (
                  <Chip label={openClaimsCount} size="small" sx={{ height: 20, fontSize: '0.75rem', fontWeight: 700, minWidth: 28, bgcolor: '#d32f2f', color: '#fff' }} />
                )}
              </Box>
            } />
          )}
        </Tabs>
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="Search by ticket#, address, phone..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ minWidth: 250, flex: 1 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={handleClearSearch}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {activeTab === 0 && (
              <FormControl size="small" sx={{ minWidth: 130 }}>
                <InputLabel>Status</InputLabel>
                <Select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} label="Status">
                  {ACTIVE_STATUSES.map((s) => (
                    <MenuItem key={s} value={s}>{s || 'All'}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {!claimsTabActive && (
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel>Priority</InputLabel>
              <Select value={filterPriority} onChange={(e) => setFilterPriority(e.target.value)} label="Priority">
                {PRIORITIES.map((p) => (
                  <MenuItem key={p} value={p}>{p || 'All'}</MenuItem>
                ))}
              </Select>
            </FormControl>
            )}

            {showTechFilter && !claimsTabActive && (
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Technician</InputLabel>
                <Select value={filterTechId} onChange={(e) => setFilterTechId(e.target.value)} label="Technician">
                  <MenuItem value="">All</MenuItem>
                  {technicians.map((t) => (
                    <MenuItem key={t.id} value={t.id}>
                      {t.firstName} {t.lastName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        </Box>
      </Paper>

      {claimsTabActive ? (
        <Paper>
          {isMobile ? (
            <Box sx={{ p: 1 }}>
              {claimsLoading ? (
                <Typography sx={{ textAlign: 'center', py: 4 }} color="text.secondary">Loading...</Typography>
              ) : pagedClaims.length === 0 ? (
                <Typography sx={{ textAlign: 'center', py: 4 }} color="text.secondary">No claims sent yet</Typography>
              ) : (
                pagedClaims.map((claim) => (
                  <Card key={claim.id} sx={{ mb: 1.5, cursor: 'pointer' }} onClick={() => openClaimDialog(claim)}>
                    <CardContent sx={{ pb: '12px !important' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Claim #{claim.claimNumber}</Typography>
                        <Chip size="small" {...claimStatusChip(claim.status)} />
                      </Box>
                      <Typography variant="body2">{claim.ticketNumber} {claim.po ? `• PO: ${claim.po}` : ''}</Typography>
                      <Typography variant="body2" color="text.secondary">{claim.address || '—'}</Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="caption" color="text.secondary">
                          Sent {formatDate(claim.sentAt)}{claim.closedAt ? ` • Closed ${formatDate(claim.closedAt)}` : ''}
                        </Typography>
                        {claim.pdfFilename && (
                          <IconButton size="small" color="primary" onClick={(e) => { e.stopPropagation(); downloadClaimPdf(claim); }}>
                            <FileDownloadIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                ))
              )}
            </Box>
          ) : (
            <TableContainer sx={table_container_base}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Claim #</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Ticket #</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>PO</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Address</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Customer</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Sent</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Closed</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: 50 }} align="center">PDF</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {claimsLoading ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 4 }}>Loading...</TableCell>
                    </TableRow>
                  ) : pagedClaims.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 4 }}>No claims sent yet</TableCell>
                    </TableRow>
                  ) : (
                    pagedClaims.map((claim) => (
                      <TableRow key={claim.id} hover sx={{ cursor: 'pointer' }} onClick={() => openClaimDialog(claim)}>
                        <TableCell sx={{ fontWeight: 700 }}>{claim.claimNumber}</TableCell>
                        <TableCell><Chip size="small" {...claimStatusChip(claim.status)} /></TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{claim.ticketNumber}</TableCell>
                        <TableCell>{claim.po || '—'}</TableCell>
                        <TableCell sx={{ maxWidth: 240 }}>
                          <Typography variant="body2" noWrap>{claim.address || '—'}</Typography>
                        </TableCell>
                        <TableCell>{claim.customer || '—'}</TableCell>
                        <TableCell>{formatDate(claim.sentAt)}</TableCell>
                        <TableCell>{claim.closedAt ? formatDate(claim.closedAt) : '—'}</TableCell>
                        <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                          {claim.pdfFilename ? (
                            <Tooltip title={`Download ${claim.pdfFilename}`} arrow>
                              <IconButton size="small" color="primary" onClick={() => downloadClaimPdf(claim)}>
                                <FileDownloadIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : '—'}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          <TablePagination
            component="div"
            count={filteredClaims.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50]}
          />
        </Paper>
      ) : isMobile ? (
        <Box>
          {loading ? (
            <Typography sx={{ textAlign: 'center', py: 4 }} color="text.secondary">Loading...</Typography>
          ) : tickets.length === 0 ? (
            <Typography sx={{ textAlign: 'center', py: 4 }} color="text.secondary">No tickets found</Typography>
          ) : (
            tickets.map(renderMobileCard)
          )}
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50]}
          />
        </Box>
      ) : (
        <Paper>
          <TableContainer sx={table_container_base}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Ticket #</TableCell>
                  {activeTab === 0 && <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>}
                  <TableCell sx={{ fontWeight: 700 }}>Priority</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Work Status</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Address</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Customer</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Brand / Year</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Created By</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{activeTab === 1 ? 'Resolved' : 'Created'}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Parts</TableCell>
                  {activeTab === 0 && <TableCell sx={{ fontWeight: 700, width: 70 }}>PO</TableCell>}
                  {activeTab === 0 && <TableCell sx={{ fontWeight: 700, width: 50 }} align="center">Label</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={activeTab === 0 ? 12 : 9} align="center" sx={{ py: 4 }}>
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : tickets.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={activeTab === 0 ? 12 : 9} align="center" sx={{ py: 4 }}>
                      No tickets found
                    </TableCell>
                  </TableRow>
                ) : (
                  tickets.map((ticket) => (
                    <TableRow
                      key={ticket.id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => openTicketDialog(ticket.id)}
                    >
                      <TableCell sx={{ fontWeight: 600 }}>{ticket.ticketNumber}</TableCell>
                      {activeTab === 0 && (
                        <TableCell>
                          <Chip
                            label={ticket.status}
                            color={statusColor[ticket.status] || 'default'}
                            size="small"
                          />
                        </TableCell>
                      )}
                      <TableCell>
                        <Chip
                          label={ticket.priority}
                          size="small"
                          sx={{
                            backgroundColor: priorityColor[ticket.priority],
                            color: '#fff',
                            fontWeight: 600,
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        {ticket.workStatus ? (
                          <Tooltip
                            title={ticket.partsLocation ? `Parts Location: ${ticket.partsLocation}` : ''}
                            arrow
                            placement="top"
                            disableHoverListener={!ticket.partsLocation}
                          >
                            <Chip
                              label={ticket.workStatus}
                              size="small"
                              color={workStatusChip[ticket.workStatus]?.color || 'default'}
                              variant={workStatusChip[ticket.workStatus]?.variant || 'outlined'}
                            />
                          </Tooltip>
                        ) : '—'}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 200 }}>
                        <Typography variant="body2" noWrap>{getAddress(ticket)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{getCustomerName(ticket)}</Typography>
                        {ticket.customerPhone && (
                          <Typography variant="caption" color="text.secondary">{ticket.customerPhone}</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap>{[ticket.brandModel, ticket.installationYear].filter(Boolean).join(' • ') || '—'}</Typography>
                      </TableCell>
                      <TableCell>{getCreatedByName(ticket)}</TableCell>
                      <TableCell>{activeTab === 1 ? formatDate(ticket.resolvedAt) : formatDate(ticket.createdAt)}</TableCell>
                      <TableCell>
                        {ticket.parts && ticket.parts.length > 0 ? (
                          <Box>
                            <Typography variant="caption">{ticket.parts.length} part(s)</Typography>
                            {ticket.parts[0]?.expectedArrival && (
                              <Typography variant="caption" display="block" color="warning.main">
                                ETA: {formatDate(ticket.parts[0].expectedArrival)}
                              </Typography>
                            )}
                          </Box>
                        ) : '—'}
                      </TableCell>
                      {activeTab === 0 && (
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Tooltip title="Create Service Proposal" arrow>
                            <IconButton size="small" color="primary" onClick={(e) => handleCreatePO(e, ticket.id)}>
                              <PostAddIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      )}
                      {activeTab === 0 && (
                        <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                          <Tooltip title="Print 4×6 Label" arrow>
                            <IconButton size="small" onClick={(e) => openLabelDialog(e, ticket)}>
                              <PrintIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50]}
          />
        </Paper>
      )}

      <WarrantyTicketDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        ticketId={selectedTicketId}
        onUpdate={fetchTickets}
      />

      <Dialog open={Boolean(selectedClaim)} onClose={() => setSelectedClaim(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Claim #{selectedClaim?.claimNumber}
          {selectedClaim && <Chip size="small" {...claimStatusChip(selectedClaim.status)} />}
        </DialogTitle>
        <DialogContent>
          {selectedClaim && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mb: 1 }}>
              <Typography variant="body2">
                <strong>Ticket:</strong>{' '}
                <Typography component="span" variant="body2" color="primary" sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                  onClick={() => { openTicketDialog(selectedClaim.ticketId); }}>
                  {selectedClaim.ticketNumber}
                </Typography>
              </Typography>
              <Typography variant="body2"><strong>PO:</strong> {selectedClaim.po || '—'}</Typography>
              <Typography variant="body2"><strong>Address:</strong> {selectedClaim.address || '—'}</Typography>
              {selectedClaim.customer && <Typography variant="body2"><strong>Customer:</strong> {selectedClaim.customer}</Typography>}
              <Typography variant="body2"><strong>Sent:</strong> {formatDate(selectedClaim.sentAt)}{selectedClaim.sentBy ? ` by ${selectedClaim.sentBy}` : ''}</Typography>
              {selectedClaim.sentTo && <Typography variant="body2"><strong>To:</strong> {selectedClaim.sentTo}</Typography>}
              {selectedClaim.closedAt && <Typography variant="body2"><strong>Closed:</strong> {formatDate(selectedClaim.closedAt)}</Typography>}
            </Box>
          )}
          <FormControlLabel
            control={
              <Checkbox
                checked={claimMoneyReceived}
                onChange={(e) => setClaimMoneyReceived(e.target.checked)}
                sx={{ '&.Mui-checked': { color: '#2e7d32' } }}
              />
            }
            label={<Typography sx={{ fontWeight: 600 }}>Money received</Typography>}
          />
        </DialogContent>
        <DialogActions>
          {selectedClaim?.pdfFilename && (
            <Button
              startIcon={<FileDownloadIcon />}
              onClick={() => downloadClaimPdf(selectedClaim)}
              sx={{ mr: 'auto' }}
            >
              PDF
            </Button>
          )}
          <Button onClick={() => setSelectedClaim(null)} disabled={claimSaving}>Cancel</Button>
          <Button
            variant="contained"
            onClick={saveClaim}
            disabled={claimSaving || Boolean(selectedClaim && claimMoneyReceived === Boolean(selectedClaim.moneyReceived))}
            startIcon={claimSaving ? <CircularProgress size={16} /> : null}
            color={claimMoneyReceived ? 'success' : 'primary'}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={labelDialogOpen} onClose={() => setLabelDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Replacement Date</DialogTitle>
        <DialogContent>
          <TextField
            type="date"
            fullWidth
            value={labelDate}
            onChange={(e) => setLabelDate(e.target.value)}
            sx={{ mt: 1 }}
            InputLabelProps={{ shrink: true }}
            label="Select date"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLabelDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={printLabel} startIcon={<PrintIcon />}>Print</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
